// AskLine — a line of the page that asks the assistant (pen Q66 "Ask here,
// written in the line", scene B): the request is written in the line
// itself, in the page's own type. No field, no box, no panel: a placeholder
// in the line, "Say what to make or change", a sparkle in its margin (the
// line is for the assistant), and a quiet hint after the text, "Enter asks ·
// Esc cancels".
//
// Only Enter sends (not an empty request, not offline, not past the
// ceiling: there the Limit hint shows under the line). Every other way out
// (Esc, a click elsewhere) goes up as a leave with the words as written;
// the host keeps them as plain text. Enter sends once: the line is done
// after it, and a second Enter before the host closes it does nothing.
//
// Pure: the words to start from, whether it can send and the ceiling come
// in; Enter and leaving go out as intents. The draft is view-ephemeral.

export type { AskLeave };
export { AskLine };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useRef, useState } from "react";
import { Icon } from "../../Icon/Icon.js";
import "../EditableText/EditableText.css";
import "./AskLine.css";

// How the author left the line without sending.
type AskLeave = "escape" | "blur";

const AskLine = ({
    words,
    online,
    max,
    className,
    onAsk,
    onLeave,
}: {
    // What the line starts with (empty: a fresh line; the words of a request
    // that failed to send).
    readonly words: string;
    // Offline: Enter does not send; the words stay.
    readonly online: boolean;
    // The request's ceiling: past it the Limit hint shows, Enter does not
    // send.
    readonly max: number;
    // The page's reading type (the paragraph's own class).
    readonly className?: string;
    readonly onAsk: (words: string) => void;
    readonly onLeave: (words: string, how: AskLeave) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [draft, setDraft] = useState(words);
    // Sent: nothing more goes out from this line (repeated Enter, the blur
    // of the line closing).
    const done = useRef(false);
    const over = draft.length > max;
    const label = t`Say what to make or change`;

    const ask = () => {
        const request = draft.trim();
        if (done.current || request === "" || !online || over) return;
        done.current = true;
        onAsk(request);
    };
    const leave = (how: AskLeave) => {
        if (done.current) return;
        // Esc ends the line; a blur may not (the host decides).
        if (how === "escape") done.current = true;
        onLeave(draft, how);
    };

    return (
        <div
            className={`bilda-ask-line${className === undefined ? "" : ` ${className}`}`}
        >
            <span className="bilda-ask-line__mark" aria-hidden="true">
                <Icon name="ask" size="sm" />
            </span>
            <div className="bilda-ask-line__text">
                <textarea
                    rows={1}
                    className="bilda-editable-text bilda-editable-text--input bilda-ask-line__field"
                    aria-label={label}
                    placeholder={label}
                    value={draft}
                    // biome-ignore lint/a11y/noAutofocus: the line the author just picked Ask here for is written in at once; the caret goes there.
                    autoFocus
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => leave("blur")}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            ask();
                        } else if (event.key === "Escape") {
                            event.preventDefault();
                            leave("escape");
                        }
                    }}
                />
                <span className="bilda-ask-line__hint" aria-hidden="true">
                    {t`Enter asks · Esc cancels`}
                </span>
            </div>
            {over ? (
                <p className="bilda-editable-text__limit" role="status">
                    {t`This can be up to ${max} characters`}
                </p>
            ) : null}
        </div>
    );
};
